import {
    ConfigNodeViewConfig
} from "../../../../../../core/shared/components/mvs-config-node-view/data/config-node-view.interfaces";

/**
 * Node View Configuration for bd.DunningType entity
 *
 * Entity: bd.DunningType
 *
 * Direct Children (Navigation Items):
 * - bd.DunningTypeLevel (levels) - FK: dunningType
 * - bd.DunningTypeCharge (charges) - FK: dunningType
 * - bd.DunningTypeStart (start) - FK: dunningType
 * - bd.DunningTypeStop (stop) - FK: dunningType
 * - bd.DunningTypeLevelAction (actions) - FK: dunningType
 * - bd.DunningTypeLevelTransition (transitions) - FK: dunningType
 * - bd.DunningTypeManualTransaction (manual) - FK: dunningType
 * - bd.DunningTypeOneTimePayment (onetime) - FK: dunningType
 * - bd.DunningTypeTerminationReason (terminationReasons) - FK: dunningType
 * - bd.DunningTypePauseReason (pauseReasons) - FK: dunningType
 *
 * Note: All child entities are direct children with no nested relationships
 *
 * displayField: only entities that really carry a `name` column may use 'name'.
 * bd.DunningTypeCharge, bd.DunningTypeManualTransaction and
 * bd.DunningTypeTerminationReason have none, so they render the framework-generated
 * `<attribute>DtoName` of their primary linked entity instead (see DtoMeta).
 */
export const DUNNING_TYPE_NODE_VIEW_CONFIG: ConfigNodeViewConfig = {
    root: {
        entityType: 'bd.DunningType',
        badgeLabel: 'MAHNART',
        defaultIcon: 'fa-solid fa-file-invoice-dollar'
    },
    navigationItems: [
        {
            id: 'levels',
            label: 'Stufen',
            icon: 'fa-solid fa-layer-group',
            entityType: 'bd.DunningTypeLevel',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        {
            id: 'charges',
            label: 'Gebühren',
            icon: 'fa-solid fa-money-bill-wave',
            entityType: 'bd.DunningTypeCharge',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            // bd.DunningTypeCharge has no `name`; the Transaktionstyp is its primary field.
            displayField: 'customerBillingTransactionTypeDtoName'
        },
        {
            id: 'start',
            label: 'Startbedingungen',
            icon: 'fa-solid fa-play-circle',
            entityType: 'bd.DunningTypeStart',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        {
            id: 'stop',
            label: 'Stoppbedingungen',
            icon: 'fa-solid fa-stop-circle',
            entityType: 'bd.DunningTypeStop',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        {
            id: 'actions',
            label: 'Stufenaktionen',
            icon: 'fa-solid fa-bolt',
            entityType: 'bd.DunningTypeLevelAction',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        {
            id: 'transitions',
            label: 'Stufenübergänge',
            icon: 'fa-solid fa-arrow-right-arrow-left',
            entityType: 'bd.DunningTypeLevelTransition',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        {
            id: 'manual',
            label: 'Manuelle Transaktionen',
            icon: 'fa-solid fa-hand-holding-dollar',
            entityType: 'bd.DunningTypeManualTransaction',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            // bd.DunningTypeManualTransaction has no `name`; the Transaktionstyp is its primary field.
            displayField: 'customerBillingTransactionTypeDtoName'
        },
        {
            id: 'onetime',
            label: 'Einmalzahlungen',
            icon: 'fa-solid fa-receipt',
            entityType: 'bd.DunningTypeOneTimePayment',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            displayField: 'name'
        },
        // TM-1000001933: Stornogründe (bd.DunningTypeTerminationReason) — FK: dunningType
        {
            id: 'terminationReasons',
            label: 'Stornogründe',
            icon: 'fa-solid fa-ban',
            entityType: 'bd.DunningTypeTerminationReason',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            // bd.DunningTypeTerminationReason has no `name`; it is an assignment whose
            // primary field is the referenced cm.ContractTerminationReasonType.
            displayField: 'contractTerminationReasonTypeDtoName'
        },
        // Mahnpause-Gründe (bd.DunningTypePauseReason) — FK: dunningType
        {
            id: 'pauseReasons',
            label: 'Mahnpause-Gründe',
            icon: 'fa-solid fa-circle-pause',
            entityType: 'bd.DunningTypePauseReason',
            foreignKey: 'dunningType',
            canAdd: true,
            canEdit: true,
            // bd.DunningTypePauseReason carries its own mandatory `name` (Bezeichnung).
            displayField: 'name'
        }
    ],
    terminalRelationships: {
        // Empty arrays as there are no nested relationships for these entities
        'bd.DunningTypeLevel': [],
        'bd.DunningTypeCharge': [],
        'bd.DunningTypeStart': [],
        'bd.DunningTypeStop': [],
        'bd.DunningTypeLevelAction': [],
        'bd.DunningTypeLevelTransition': [],
        'bd.DunningTypeManualTransaction': [],
        'bd.DunningTypeOneTimePayment': [],
        // TM-1000001933: Stornogründe haben keine verschachtelten Relationen
        'bd.DunningTypeTerminationReason': [],
        // Mahnpause-Gründe haben keine verschachtelten Relationen
        'bd.DunningTypePauseReason': []
    },
    displayOptions: {
        expandLabel: 'AUSKLAPPEN',
        collapseLabel: 'EINKLAPPEN',
        entriesLabel: 'Einträge',
        relationsLabel: 'Relationen',
        showMoreLabel: 'weitere anzeigen',
        showLessLabel: 'Weniger anzeigen',
        noResultsLabel: 'Keine Einträge gefunden',
        searchPlaceholder: 'Suchen...',
        newestLabel: 'Neueste',
        oldestLabel: 'Älteste'
    }
};
